// src/components/auth/MFAChallenge.jsx
// Component for verifying MFA code on login

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield, Loader2, AlertTriangle, LogOut, CheckCircle2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';

export default function MFAChallenge({ isOpen, onVerified, onSignOut }) {
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [factorId, setFactorId] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadFactors();
      setIsVerified(false);
      setIsAnimatingOut(false);
      // Focus input when dialog opens
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      // Reset states when dialog closes
      setIsVerified(false);
      setIsAnimatingOut(false);
      setVerifyCode('');
      setError('');
    }
  }, [isOpen]);

  // Auto-submit when 6th character is entered
  useEffect(() => {
    if (verifyCode.length === 6 && !verifying && !isVerified && factorId && isOpen) {
      // Small delay to ensure the 6th character is fully processed
      const timeoutId = setTimeout(() => {
        handleVerify();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [verifyCode.length, isOpen]); // Only trigger on length change and when dialog is open

  const loadFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;

      // Get the first verified TOTP factor
      const totpFactor = data.totp.find(f => f.status === 'verified');
      if (totpFactor) {
        setFactorId(totpFactor.id);
        logger.debug('Found verified TOTP factor:', totpFactor.id);
      }
    } catch (err) {
      logger.error('Error loading MFA factors:', err);
      setError('Nie udało się załadować ustawień MFA.');
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) {
      setError('Kod musi mieć 6 cyfr');
      return;
    }

    if (!factorId) {
      setError('Nie znaleziono skonfigurowanego uwierzytelniania');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      // Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      });

      if (challengeError) {
        throw challengeError;
      }

      // Verify the code
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode
      });

      if (verifyError) {
        throw verifyError;
      }

      logger.debug('MFA challenge verified successfully');
      
      // Mark as verified and start blur-to-clear animation
      setIsVerified(true);
      setIsAnimatingOut(true);
      
      // Wait for animation to complete before calling onVerified
      setTimeout(() => {
        onVerified();
      }, 500); // Match animation duration
    } catch (err) {
      logger.error('MFA challenge verification error:', err);
      if (err.message?.includes('Invalid') || err.message?.includes('invalid')) {
        setError('Nieprawidłowy kod. Sprawdź aplikację authenticator i spróbuj ponownie.');
      } else {
        setError('Weryfikacja nie powiodła się. Spróbuj ponownie.');
      }
      setVerifyCode('');
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && verifyCode.length === 6) {
      handleVerify();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton
        overlayClassName={cn(
          "transition-all duration-500 ease-in-out",
          isAnimatingOut 
            ? 'backdrop-blur-none' 
            : 'backdrop-blur-md'
        )}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4">
            <div className={`h-16 w-16 rounded-full flex items-center justify-center transition-all duration-300 ${
              isVerified 
                ? 'bg-green-100 scale-110' 
                : 'bg-blue-100'
            }`}>
              {isVerified ? (
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              ) : (
                <Shield className="h-8 w-8 text-blue-600" />
              )}
            </div>
          </div>
          <DialogTitle className="text-xl">
            {isVerified ? 'Weryfikacja pomyślna!' : 'Weryfikacja dwuskładnikowa'}
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {isVerified 
              ? 'Przygotowywanie sesji...' 
              : 'Wprowadź kod z aplikacji authenticator, aby kontynuować.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Code input */}
          <div className="space-y-2">
            <Input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={verifyCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerifyCode(val);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              className="text-center text-2xl tracking-[0.5em] font-mono h-14"
              autoComplete="one-time-code"
              disabled={verifying}
            />
            {error && (
              <p className="text-sm text-red-600 flex items-center justify-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                {error}
              </p>
            )}
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <p className="text-blue-700">
              Otwórz aplikację authenticator (Google Authenticator, Microsoft Authenticator itp.) i wprowadź wyświetlony 6-cyfrowy kod.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button
            onClick={handleVerify}
            disabled={verifying || verifyCode.length !== 6}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            {verifying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Weryfikacja...
              </>
            ) : (
              'Zweryfikuj'
            )}
          </Button>

          <Button
            variant="ghost"
            onClick={onSignOut}
            className="w-full text-slate-500"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Wyloguj się
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
